import React from "react";
import {
    Avatar,
    Dialog,
    DialogContent,
    Box,
    Stack,
    Typography,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { QR, QrModalHeader, qrDialogPaperSx, QrActionButton } from "./qrUi";
import Slide from "@mui/material/Slide";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import DownloadIcon from '@mui/icons-material/Download';
import { useSelector } from "react-redux";
import * as XLSX from 'xlsx';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

// Stable soft colour per name for the avatar initial
const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#db2777", "#d97706", "#198754", "#4f46e5"];
const avatarColor = (name) => {
    const s = String(name || "");
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// qrTitle / rangeLabel are optional (shown in the header subtitle)
export default function IndividualData({ show, handleClose, qrTitle, rangeLabel }) {
    const { qrIndividualData, status1 } = useSelector((state) => state.qrCode);
    const contacts = Array.isArray(qrIndividualData) ? qrIndividualData : [];

    const columns = [
        { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
        {
            accessorKey: "contact_name",
            header: "Name",
            Cell: ({ row }) => {
                const name = row.original.contact_name || "-";
                const c = avatarColor(name);
                return (
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ justifyContent: "flex-start", textAlign: "left" }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12.5, fontWeight: 800, color: c, bgcolor: `${c}1a` }}>
                            {String(name).trim().charAt(0).toUpperCase() || "?"}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} color="#0f172a" noWrap title={name}>
                            {name}
                        </Typography>
                    </Stack>
                );
            },
        },
        {
            accessorKey: "contact_mobile",
            header: "Mobile",
            Cell: ({ row }) =>
                row.original.contact_mobile ? (
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ justifyContent: "center" }}>
                        <PhoneIphoneIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                        <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#334155" }}>
                            {row.original.contact_mobile}
                        </Typography>
                    </Stack>
                ) : "-",
        },
        {
            accessorKey: "inserted_time",
            header: "Created Time",
            Cell: ({ row }) => (
                <Typography variant="body2" color="text.secondary">
                    {formatDate(row.original.inserted_time)}
                </Typography>
            ),
        },
    ];

    const getValue = (val, fallback = "-") => val !== null && val !== undefined && val !== "" ? val : fallback;

    const handleQrExcelDownload = () => {
        const formattedData = qrIndividualData.map((item) => ({

            "Name": getValue(item.contact_name),
            "Mobile": getValue(item.contact_mobile),
            // "Email": getValue(item.contact_email),
            "Inserted Time":
                item.inserted_time ? formatDate(item.inserted_time) : "-"
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, 'qrReports.xlsx');
    };

    const subtitle = [qrTitle, rangeLabel].filter(Boolean).join(" · ") || "Contacts captured through this QR code";

    // Toolbar content next to the search: contacts count chip + Download Excel (right)
    const toolbar = (
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ minHeight: 44 }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{ px: 1.5, height: 36, borderRadius: 5, bgcolor: `${QR.main}12`, border: `1px solid ${QR.main}33`, color: QR.main }}
            >
                <PeopleAltIcon sx={{ fontSize: 17 }} />
                <Typography variant="body2" fontWeight={800}>
                    {contacts.length}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: "#475569" }}>
                    {contacts.length === 1 ? "contact" : "contacts"}
                </Typography>
            </Stack>
            <Box sx={{ ml: { sm: "auto" } }}>
                <QrActionButton
                    icon={<DownloadIcon sx={{ fontSize: 17 }} />}
                    label="Download Excel"
                    color="#d97706"
                    onClick={handleQrExcelDownload}
                />
            </Box>
        </Stack>
    );

    return (
        <Dialog
            open={show}
            onClose={handleClose}
            TransitionComponent={Transition}
            fullWidth
            maxWidth="md"
            keepMounted
            PaperProps={{ sx: qrDialogPaperSx }}
        >
            <QrModalHeader
                icon={<PeopleAltIcon />}
                title="Individual Data"
                subtitle={subtitle}
                onClose={() => handleClose()}
            />

            <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: "#fff" }}>
                {/* Same modern table as the QR list: search left, count + Excel right */}
                <DataTableWithCheckbox
                    status={status1}
                    enableRowSelection={false}
                    columns={columns}
                    data={contacts}
                    renderFilters={toolbar}
                    searchFirst
                    emptyMessage="No contacts captured in this period."
                />
            </DialogContent>
        </Dialog>
    );
}
